/**
 * Stackable install equipment periods (crane / telehandler / scissor / basket).
 * Legacy day × $/day fields still apply when a required machine has no lines.
 */

import type {
  InstallEquipmentKind,
  InstallEquipmentLine,
  InstallEquipmentPeriod,
  InstallNodeData,
} from '../types';
import type { ProposalRateSettings } from './appSettings';

export const INSTALL_EQUIPMENT_KINDS: InstallEquipmentKind[] = [
  'crane',
  'telehandler',
  'scissorLift',
  'basketLift',
];

export const INSTALL_EQUIPMENT_LABELS: Record<InstallEquipmentKind, string> = {
  crane: 'Crane',
  telehandler: 'Telehandler',
  scissorLift: 'Scissor Lift',
  basketLift: 'Basket Lift',
};

export const PERIODS_FOR_KIND: Record<InstallEquipmentKind, InstallEquipmentPeriod[]> = {
  crane: ['hour', 'day', 'week', 'month'],
  telehandler: ['day', 'week', 'month'],
  scissorLift: ['day', 'week', 'month'],
  basketLift: ['day', 'week', 'month'],
};

export function isEquipmentKindRequired(
  install: InstallNodeData,
  kind: InstallEquipmentKind,
): boolean {
  switch (kind) {
    case 'crane':
      return !!install.craneRequired;
    case 'telehandler':
      return !!install.telehandlerRequired;
    case 'scissorLift':
      return !!install.scissorLiftRequired;
    case 'basketLift':
      return !!install.basketLiftRequired;
  }
}

export function setEquipmentKindRequired(
  install: InstallNodeData,
  kind: InstallEquipmentKind,
  required: boolean,
): InstallNodeData {
  switch (kind) {
    case 'crane':
      return { ...install, craneRequired: required };
    case 'telehandler':
      return { ...install, telehandlerRequired: required };
    case 'scissorLift':
      return { ...install, scissorLiftRequired: required };
    case 'basketLift':
      return { ...install, basketLiftRequired: required };
  }
}

export function linesForKind(
  install: InstallNodeData,
  kind: InstallEquipmentKind,
): InstallEquipmentLine[] {
  return (install.equipmentLines || []).filter(line => line.kind === kind);
}

/** Missing `units` is one machine so older proposals keep the same total. */
export function equipmentMachineCount(
  line: Pick<InstallEquipmentLine, 'units'>,
): number {
  if (line.units == null) return 1;
  const units = Number(line.units);
  return Number.isFinite(units) && units > 0 ? units : 0;
}

export function equipmentLineTotal(
  line: Pick<InstallEquipmentLine, 'quantity' | 'rate' | 'units'>,
): number {
  const qty = Number(line.quantity);
  const rate = Number(line.rate);
  const machines = equipmentMachineCount(line);
  return machines * (Number.isFinite(qty) ? qty : 0) * (Number.isFinite(rate) ? rate : 0);
}

export function basketLiftDefaultRate(rates?: Pick<ProposalRateSettings, 'basketLiftDailyRate'> | null): number {
  const rate = rates?.basketLiftDailyRate;
  return typeof rate === 'number' && Number.isFinite(rate) && rate >= 0 ? rate : 1500;
}

export function sumKindEquipmentRaw(
  install: InstallNodeData,
  kind: InstallEquipmentKind,
  rates?: Pick<ProposalRateSettings, 'basketLiftDailyRate'> | null,
): number {
  if (!isEquipmentKindRequired(install, kind)) return 0;
  const lines = linesForKind(install, kind);
  if (lines.length > 0) {
    return lines.reduce((sum, line) => sum + equipmentLineTotal(line), 0);
  }
  switch (kind) {
    case 'crane':
      return (install.craneDaysNeeded || 0) * (install.craneRatePerDay || 0);
    case 'telehandler':
      return (install.telehandlerDaysNeeded || 0) * (install.telehandlerRatePerDay || 0);
    case 'scissorLift':
      return (install.scissorLiftDaysNeeded || 0) * (install.scissorLiftRatePerDay || 0);
    case 'basketLift':
      return basketLiftDefaultRate(rates);
  }
}

export function sumInstallEquipmentRaw(
  install: InstallNodeData,
  rates?: Pick<ProposalRateSettings, 'basketLiftDailyRate'> | null,
): number {
  return INSTALL_EQUIPMENT_KINDS.reduce(
    (sum, kind) => sum + sumKindEquipmentRaw(install, kind, rates),
    0,
  );
}

export function defaultRateForNewLine(
  kind: InstallEquipmentKind,
  period: InstallEquipmentPeriod,
  overtime: boolean,
  install: InstallNodeData,
  rates: ProposalRateSettings,
): number {
  if (kind === 'crane' && overtime) return rates.craneOvertimeHourlyRate || 0;
  if (kind === 'crane' && period === 'hour') return rates.craneHourlyRate || 0;
  if (period !== 'day') return 0;
  switch (kind) {
    case 'crane':
      return install.craneRatePerDay || rates.craneDailyRate || 0;
    case 'telehandler':
      return install.telehandlerRatePerDay || rates.telehandlerDailyRate || 0;
    case 'scissorLift':
      return install.scissorLiftRatePerDay || rates.scissorLiftDailyRate || 0;
    case 'basketLift':
      return basketLiftDefaultRate(rates);
  }
}

function defaultQuantityForNewLine(
  kind: InstallEquipmentKind,
  period: InstallEquipmentPeriod,
  overtime: boolean,
  install: InstallNodeData,
): number {
  if (overtime || period !== 'day') return 1;
  if (linesForKind(install, kind).length > 0) return 1;
  if (kind === 'crane' && (install.craneDaysNeeded || 0) > 0) return install.craneDaysNeeded;
  if (kind === 'telehandler' && (install.telehandlerDaysNeeded || 0) > 0) return install.telehandlerDaysNeeded;
  if (kind === 'scissorLift' && (install.scissorLiftDaysNeeded || 0) > 0) return install.scissorLiftDaysNeeded;
  return 1;
}

export function createEquipmentLine(
  kind: InstallEquipmentKind,
  install: InstallNodeData,
  rates: ProposalRateSettings,
  opts?: { period?: InstallEquipmentPeriod; overtime?: boolean },
): InstallEquipmentLine {
  const overtime = !!opts?.overtime && kind === 'crane';
  const period: InstallEquipmentPeriod = overtime ? 'hour' : (opts?.period || 'day');
  return {
    id: `eq-${kind}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    kind,
    period,
    units: 1,
    quantity: defaultQuantityForNewLine(kind, period, overtime, install),
    rate: defaultRateForNewLine(kind, period, overtime, install, rates),
    ...(overtime ? { overtime: true } : {}),
  };
}

export function ensureKindSeeded(
  install: InstallNodeData,
  kind: InstallEquipmentKind,
  rates: ProposalRateSettings,
): InstallNodeData {
  if (linesForKind(install, kind).length > 0) return install;
  return {
    ...install,
    equipmentLines: [...(install.equipmentLines || []), createEquipmentLine(kind, install, rates)],
  };
}

export function toggleEquipmentKind(
  install: InstallNodeData,
  kind: InstallEquipmentKind,
  required: boolean,
  rates: ProposalRateSettings,
): InstallNodeData {
  const next = setEquipmentKindRequired(install, kind, required);
  if (!required) return next;
  return ensureKindSeeded(next, kind, rates);
}

export function upsertEquipmentLine(
  install: InstallNodeData,
  lineId: string,
  patch: Partial<Pick<InstallEquipmentLine, 'period' | 'units' | 'quantity' | 'rate' | 'overtime'>>,
): InstallNodeData {
  return {
    ...install,
    equipmentLines: (install.equipmentLines || []).map(line => {
      if (line.id !== lineId) return line;
      const next = { ...line, ...patch };
      if (next.overtime) next.period = 'hour';
      return next;
    }),
  };
}

export function removeEquipmentLine(install: InstallNodeData, lineId: string): InstallNodeData {
  return {
    ...install,
    equipmentLines: (install.equipmentLines || []).filter(line => line.id !== lineId),
  };
}

export function addEquipmentLine(
  install: InstallNodeData,
  kind: InstallEquipmentKind,
  rates: ProposalRateSettings,
  opts?: { period?: InstallEquipmentPeriod; overtime?: boolean },
): InstallNodeData {
  return {
    ...install,
    equipmentLines: [...(install.equipmentLines || []), createEquipmentLine(kind, install, rates, opts)],
  };
}

export function formatEquipmentPeriodUnit(
  period: InstallEquipmentPeriod,
  overtime?: boolean,
): string {
  if (overtime) return 'OT hr';
  switch (period) {
    case 'hour':
      return 'hr';
    case 'day':
      return 'day';
    case 'week':
      return 'week';
    case 'month':
      return 'month';
  }
}

export function formatEquipmentLineAudit(line: InstallEquipmentLine): string {
  const unit = formatEquipmentPeriodUnit(line.period, line.overtime);
  const machines = equipmentMachineCount(line);
  const qty = Number(line.quantity) || 0;
  const rate = Number(line.rate) || 0;
  const money = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(rate);
  const duration = `${qty} ${unit} × ${money}`;
  return machines === 1 ? duration : `${machines} × ${duration}`;
}

export type InstallEquipmentAuditLine = {
  kind: InstallEquipmentKind;
  label: string;
  amount: number;
};

export function listInstallEquipmentAuditLines(
  install: InstallNodeData,
  rates?: Pick<ProposalRateSettings, 'basketLiftDailyRate'> | null,
): InstallEquipmentAuditLine[] {
  const rows: InstallEquipmentAuditLine[] = [];
  for (const kind of INSTALL_EQUIPMENT_KINDS) {
    if (!isEquipmentKindRequired(install, kind)) continue;
    const name = INSTALL_EQUIPMENT_LABELS[kind];
    const lines = linesForKind(install, kind);
    if (lines.length > 0) {
      for (const line of lines) {
        rows.push({
          kind,
          label: `${name}: ${formatEquipmentLineAudit(line)}`,
          amount: equipmentLineTotal(line),
        });
      }
      continue;
    }
    const amount = sumKindEquipmentRaw(install, kind, rates);
    if (kind === 'basketLift') {
      rows.push({ kind, label: `${name}: 1 day × $${basketLiftDefaultRate(rates).toLocaleString('en-US')}`, amount });
      continue;
    }
    const days =
      kind === 'crane'
        ? install.craneDaysNeeded
        : kind === 'telehandler'
          ? install.telehandlerDaysNeeded
          : install.scissorLiftDaysNeeded;
    const rate =
      kind === 'crane'
        ? install.craneRatePerDay
        : kind === 'telehandler'
          ? install.telehandlerRatePerDay
          : install.scissorLiftRatePerDay;
    const money = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(rate || 0);
    rows.push({ kind, label: `${name}: ${days || 0}d @ ${money}/day`, amount });
  }
  return rows;
}
